"use client";

import { useState } from "react";
import { subscribeNewsletter } from "@/lib/actions/community";
import { adsConversion } from "@/lib/gtag";

/** Newsletter signup — card (updates page) and inline (footer) variants.
 * Weekly-ish release summaries; unsubscribe link in every email. */
export function NewsletterSignup({ variant = "card" }: { variant?: "card" | "inline" }) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setState("busy");
    try {
      const res = await subscribeNewsletter(email, company);
      if (res.ok) adsConversion("newsletter");
      setState(res.ok ? "done" : "error");
    } catch {
      setState("error");
    }
  }

  if (state === "done") {
    return (
      <p className={variant === "card" ? "text-[14px] font-semibold text-green" : "text-[12.5px] font-semibold text-green"}>
        You&apos;re subscribed — updates land roughly weekly. Every email has an unsubscribe link.
      </p>
    );
  }

  const compact = variant === "inline";
  return (
    <form onSubmit={submit} className="flex max-w-[420px] flex-wrap items-center gap-2">
      {/* Honeypot: hidden from people, filled by bots. */}
      <input
        type="text"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        className="absolute left-[-9999px] h-px w-px opacity-0"
      />
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        aria-label="Email address"
        className={`min-w-0 flex-1 rounded-[9px] border border-border bg-paper px-3 text-ink outline-none focus:outline-2 focus:outline-copper ${
          compact ? "h-[34px] text-[12.5px]" : "h-[40px] text-[13.5px]"
        }`}
      />
      <button
        type="submit"
        disabled={state === "busy"}
        className={`shrink-0 rounded-[9px] bg-green font-bold text-surface disabled:opacity-50 ${
          compact ? "h-[34px] px-3 text-[12px]" : "h-[40px] px-4 text-[13px]"
        }`}
      >
        {state === "busy" ? "Subscribing…" : "Subscribe"}
      </button>
      {state === "error" && (
        <p className="w-full text-[12.5px] font-semibold text-danger" role="alert">
          That didn&apos;t work — check the address and try again.
        </p>
      )}
    </form>
  );
}
